import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SurveyPublic() {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/surveys/${token}`)
      .then((r) => {
        setSurvey(r.data);
        if (r.data.completed) setDone(true);
      })
      .catch(() => setError("Sondaggio non trovato o non più valido."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = survey.questions.map((q, i) => ({
        type: q.type, question: q.text, value: answers[i] ?? "",
      }));
      await axios.post(`${API}/public/surveys/${token}/submit`, { answers: payload });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore durante l'invio");
    }
  };

  if (error) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-8 max-w-sm w-full text-center text-sm text-zinc-600" data-testid="survey-error">{error}</div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-8 max-w-sm w-full text-center" data-testid="survey-thanks">
        <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-3" />
        <h1 className="font-display font-bold text-xl text-zinc-900">Grazie!</h1>
        <p className="text-sm text-zinc-500 mt-1">Il tuo feedback è stato registrato. A presto!</p>
      </div>
    </div>
  );

  if (!survey) return <div className="min-h-screen bg-zinc-100" />;

  return (
    <div className="min-h-screen bg-zinc-100 py-8 px-4">
      <form onSubmit={submit} className="max-w-lg mx-auto bg-white border border-zinc-200 rounded-lg p-6 space-y-5" data-testid="survey-form">
        <div>
          <div className="font-display font-extrabold text-lg text-zinc-900">DISHENDA</div>
          <h1 className="font-display font-bold text-2xl text-zinc-900 mt-2">{survey.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Ciao {survey.customer_first_name}, {survey.description || `grazie per essere stat${"o/a"} da ${survey.restaurant_name}.`}
          </p>
        </div>
        {survey.questions.map((q, i) => (
          <div key={i} className="space-y-2">
            <label className="text-sm font-semibold text-zinc-900">{q.text}</label>
            {q.type === "rating" && (
              <div className="flex gap-2" data-testid={`q-rating-${i}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setAnswers({ ...answers, [i]: n })}
                    data-testid={`q-rating-${i}-${n}`}
                    className={`w-11 h-11 rounded-md border-2 transition ${answers[i] >= n ? "border-amber-400 bg-amber-50" : "border-zinc-200"}`}>
                    <Star size={20} weight={answers[i] >= n ? "fill" : "regular"}
                      className={`mx-auto ${answers[i] >= n ? "text-amber-500" : "text-zinc-300"}`} />
                  </button>
                ))}
              </div>
            )}
            {q.type === "rating10" && (
              <div data-testid={`q-rating10-${i}`}>
                <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5">
                  {[...Array(11).keys()].map((n) => (
                    <button type="button" key={n} onClick={() => setAnswers({ ...answers, [i]: n })}
                      data-testid={`q-rating10-${i}-${n}`}
                      className={`h-10 rounded-md border-2 text-sm font-bold transition ${
                        answers[i] === n ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}>
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
                  <span>Per niente</span><span>Assolutamente sì</span>
                </div>
              </div>
            )}
            {q.type === "choice" && (
              <div className="grid gap-2" data-testid={`q-choice-${i}`}>
                {q.options.map((opt) => (
                  <button type="button" key={opt} onClick={() => setAnswers({ ...answers, [i]: opt })}
                    data-testid={`q-choice-${i}-${opt}`}
                    className={`h-11 px-3 rounded-md border-2 text-sm text-left font-medium transition ${
                      answers[i] === opt ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-700"}`}>
                    {opt}
                  </button>
                ))}
              </div>
            )}
            {(q.type === "open" || q.type === "suggestion") && (
              <textarea rows={3} value={answers[i] || ""} onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                data-testid={`q-open-${i}`} placeholder="Scrivi qui..."
                className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            )}
          </div>
        ))}
        <button type="submit" data-testid="survey-submit"
          className="w-full h-12 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 active:scale-[0.98] transition">
          Invia risposte
        </button>
      </form>
    </div>
  );
}
